import {
    mdiAlertCircle,
    mdiBankOutline,
    mdiCheck,
    mdiClose,
    mdiHistory,
    mdiMagnify,
    mdiSubdirectoryArrowRight,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { CatalogPayer } from '../../services/worker-configuration/automation/worker-automation.types';
import { getUsageBadgePresentation } from '../../services/worker-configuration/automation/worker-automation.utils';
import { WorkerStore } from '../../stores/worker-configuration/worker.store';
import * as S from './configuration-rule.styles';

interface IConfigurationRulePayerEditorProps {
    disabled?: boolean;
    matchIndex: number;
    payerIds: string[];
    ruleId: string;
    store: WorkerStore;
}

export const ConfigurationRulePayerEditor: React.FC<IConfigurationRulePayerEditorProps> = observer(
    ({ disabled, matchIndex, payerIds, ruleId, store }) => {
        const pickerKey = `${ruleId}|${matchIndex}|payer`;
        const isOpen = store.openPickerKey === pickerKey;
        const hasSelections = payerIds.length > 0;
        const selectedIds = useMemo(() => new Set(payerIds), [payerIds]);
        const pickerPanelRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (!isOpen) {
                return;
            }

            const delay = store.pickerQuery.trim() ? 250 : 0;
            const handle = window.setTimeout(() => {
                void store.searchPickerPayers(store.pickerQuery);
            }, delay);

            return () => window.clearTimeout(handle);
        }, [isOpen, store, store.pickerQuery]);

        useEffect(() => {
            if (!isOpen || !pickerPanelRef.current) {
                return;
            }

            pickerPanelRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, [isOpen, store.pickerPayerOptions.length, store.pickerSearchLoading]);

        const options = isOpen
            ? store.pickerPayerOptions
                  .filter(payer => !selectedIds.has(payer.payerId))
                  .map(payer => ({
                      payer,
                      badge: getUsageBadgePresentation(
                          store.getCatalogUsage(ruleId, matchIndex, 'payerId', payer.payerId),
                      ),
                  }))
            : [];

        const handlePick = (payer: CatalogPayer): void => {
            store.addPayer(ruleId, matchIndex, payer);
        };

        const showPickerPanel = isOpen && !disabled;
        const showLoading = store.pickerSearchLoading;
        const showNoResults = !showLoading && options.length === 0 && store.pickerQuery.trim() !== '';

        return (
            <S.PayerEditor>
                <S.PayerSectionHint>
                    <Icon path={mdiSubdirectoryArrowRight} size={0.75} color="#8e9293" aria-hidden={true} />
                    Matches any of these payers (OR)
                </S.PayerSectionHint>

                {payerIds.map((payerId, index) => {
                    const payer = store.payerById[payerId];
                    const usage = getUsageBadgePresentation(
                        store.getCatalogUsage(ruleId, matchIndex, 'payerId', payerId),
                    );

                    return (
                        <React.Fragment key={payerId}>
                            {index > 0 && (
                                <S.PayerOrDivider>
                                    <S.PayerOrLabel>OR</S.PayerOrLabel>
                                    <S.PayerOrLine />
                                </S.PayerOrDivider>
                            )}

                            {payer ? (
                                <S.PayerCard>
                                    <S.PayerCardIcon>
                                        <Icon path={mdiBankOutline} size={1.2} aria-hidden={true} />
                                    </S.PayerCardIcon>
                                    <S.PayerCardBody>
                                        <S.PayerCardName>{payer.name}</S.PayerCardName>
                                        <S.PayerCardMeta>{payer.meta}</S.PayerCardMeta>
                                    </S.PayerCardBody>
                                    {usage.used && (
                                        <S.UsageBadge variant="used">
                                            <Icon path={mdiHistory} size={0.65} aria-hidden={true} />
                                            {usage.short}
                                        </S.UsageBadge>
                                    )}
                                    <S.PayerCardRemove
                                        type="button"
                                        disabled={disabled}
                                        aria-label={`Remove ${payer.name}`}
                                        onClick={() => store.removePayer(ruleId, matchIndex, payerId)}>
                                        <Icon path={mdiClose} size={0.7} aria-hidden={true} />
                                    </S.PayerCardRemove>
                                </S.PayerCard>
                            ) : (
                                <S.PayerMissingCard>
                                    <Icon path={mdiAlertCircle} size={0.85} color="#C47A17" aria-hidden={true} />
                                    <S.PayerMissingText>
                                        Payer <S.PayerMissingId>{payerId}</S.PayerMissingId> could not be resolved.
                                    </S.PayerMissingText>
                                    <S.PayerCardRemove
                                        type="button"
                                        disabled={disabled}
                                        aria-label={`Remove unresolved payer ${payerId}`}
                                        onClick={() => store.removePayer(ruleId, matchIndex, payerId)}>
                                        <Icon path={mdiClose} size={0.7} aria-hidden={true} />
                                    </S.PayerCardRemove>
                                </S.PayerMissingCard>
                            )}
                        </React.Fragment>
                    );
                })}

                <S.PayerSearchWrap>
                    <Icon path={mdiMagnify} size={0.6} color="#8e9293" aria-hidden={true} />
                    <S.ChipInput
                        placeholder={hasSelections ? 'Add another payer (OR)…' : 'Search payers by name or ID…'}
                        disabled={disabled}
                        readOnly={disabled}
                        value={isOpen ? store.pickerQuery : ''}
                        onFocus={() => {
                            if (!disabled) {
                                store.setPickerState(pickerKey, '');
                            }
                        }}
                        onChange={event => {
                            if (!disabled) {
                                store.setPickerState(pickerKey, event.target.value);
                            }
                        }}
                        onBlur={() => {
                            window.setTimeout(() => {
                                if (store.openPickerKey === pickerKey) {
                                    store.setPickerState(null);
                                }
                            }, 170);
                        }}
                        onKeyDown={event => {
                            if (disabled) {
                                return;
                            }

                            if (event.key === 'Enter' && options[0]) {
                                event.preventDefault();
                                handlePick(options[0].payer);
                            }
                        }}
                    />
                </S.PayerSearchWrap>

                {showPickerPanel && (
                    <S.PayerPickerPanel ref={pickerPanelRef}>
                        <S.PayerPickerDropdown>
                            {showLoading ? (
                                <S.PayerPickerOption style={{ cursor: 'default' }}>
                                    <S.PayerPickerName>Searching payers…</S.PayerPickerName>
                                </S.PayerPickerOption>
                            ) : options.length > 0 ? (
                                options.map(({ payer, badge }) => (
                                    <S.PayerPickerOption
                                        key={payer.payerId}
                                        onMouseDown={event => {
                                            event.preventDefault();
                                            handlePick(payer);
                                        }}>
                                        <S.PayerPickerName>{payer.name}</S.PayerPickerName>
                                        <S.PayerPickerSub>{payer.meta}</S.PayerPickerSub>
                                        {badge.used ? (
                                            <S.UsageBadge variant="used">
                                                <Icon path={mdiHistory} size={0.65} aria-hidden={true} />
                                                {badge.label}
                                            </S.UsageBadge>
                                        ) : (
                                            <S.UsageBadge variant="fresh">
                                                <Icon path={mdiCheck} size={0.65} aria-hidden={true} />
                                                {badge.label}
                                            </S.UsageBadge>
                                        )}
                                    </S.PayerPickerOption>
                                ))
                            ) : showNoResults ? (
                                <S.PayerPickerOption style={{ cursor: 'default' }}>
                                    <S.PayerPickerName>No payers found</S.PayerPickerName>
                                </S.PayerPickerOption>
                            ) : (
                                <S.PayerPickerOption style={{ cursor: 'default' }}>
                                    <S.PayerPickerName>No payers available</S.PayerPickerName>
                                </S.PayerPickerOption>
                            )}
                        </S.PayerPickerDropdown>
                    </S.PayerPickerPanel>
                )}
            </S.PayerEditor>
        );
    },
);
